import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  ArrowLeft,
  ChevronRight,
  Plus,
  Minus,
  Search,
  Compass,
  Wrench,
  Activity,
  TrendingUp,
  Sparkles,
  Star,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import { getIndustryBySlug, INDUSTRY_LIST } from "../data/industryData";
import { useMarket } from "../lib/useMarket";
import { MarketPill, MarketSwitcher } from "../components/shared/MarketSwitcher";

/* ─── Process Steps ──────────────────────────────────────────────── */
const PROCESS_STEPS = [
  {
    n: "01",
    icon: Search,
    title: "Free Audit",
    text: "We audit your current digital presence — SEO, ads, social, reputation and AI visibility — and identify the highest-impact gaps.",
  },
  {
    n: "02",
    icon: Compass,
    title: "Custom Strategy",
    text: "A bespoke marketing plan built for your industry, market and revenue targets. No cookie-cutter templates.",
  },
  {
    n: "03",
    icon: Wrench,
    title: "Build & Launch",
    text: "We build the campaigns, content, automations and landing pages — then launch everything on an agreed timeline.",
  },
  {
    n: "04",
    icon: Activity,
    title: "Optimise",
    text: "Weekly performance reviews, A/B testing and continuous creative iteration. We move fast on what's working and cut what's not.",
  },
  {
    n: "05",
    icon: TrendingUp,
    title: "Scale",
    text: "Once the core engine is proven, we scale spend and content output — compounding the results month over month.",
  },
];

/* ─── FAQ Accordion ─────────────────────────────────────────────── */
function FAQAccordion({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <ul className="border-t border-zinc-200">
      {items.map((f, idx) => {
        const isOpen = open === idx;
        return (
          <li key={f.q} className="border-b border-zinc-200">
            <button
              onClick={() => setOpen(isOpen ? -1 : idx)}
              className="w-full py-6 flex items-center justify-between gap-6 text-left group"
              aria-expanded={isOpen}
            >
              <span className="font-display text-lg md:text-xl font-bold text-charcoal group-hover:text-electric transition-colors">
                {f.q}
              </span>
              <span
                className={`shrink-0 w-9 h-9 grid place-items-center rounded-xl border transition-colors ${
                  isOpen
                    ? "bg-electric border-electric text-white"
                    : "border-zinc-200 text-charcoal group-hover:border-electric"
                }`}
              >
                {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </span>
            </button>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="pb-6 pr-12"
              >
                <p className="text-zinc-600 leading-relaxed text-[15px]">{f.a}</p>
              </motion.div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────── */
export default function IndustryPage() {
  const { market, code, slug, isLocalised, hreflangs } = useMarket();
  const industry = getIndustryBySlug(slug);

  /* 404 fallback */
  if (!industry) {
    return (
      <>
        <Navbar />
        <main className="pt-40 pb-40 text-center bg-white">
          <p className="text-overline text-electric mb-3">404</p>
          <h1 className="font-display text-4xl font-extrabold text-charcoal mb-6">
            Industry page not found
          </h1>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3 font-semibold rounded-xl hover:bg-electric-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const related = (industry.relatedSlugs || [])
    .map((s) => INDUSTRY_LIST.find((i) => i.slug === s))
    .filter(Boolean);

  // ─── Localisation ───────────────────────────────────────
  const pageUrl = isLocalised
    ? `https://lumotis.com/${code}/${industry.slug}`
    : `https://lumotis.com/${industry.slug}`;

  const localisedTitle = isLocalised
    ? industry.metaTitle.replace(/\| Lumotis$/, `in ${market.name} | Lumotis`)
    : industry.metaTitle;

  const localisedDesc = isLocalised
    ? `${industry.metaDescription} Serving ${market.name} ${industry.shortName.toLowerCase()} businesses with localised strategy.`
    : industry.metaDescription;

  const hrefAlternates = hreflangs();

  return (
    <>
      <Helmet>
        {isLocalised && <html lang={market.locale.split("_")[0]} />}
        <title>{localisedTitle}</title>
        <meta name="description" content={localisedDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={localisedTitle} />
        <meta property="og:description" content={localisedDesc} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        {isLocalised && <meta property="og:locale" content={market.locale} />}
        <link rel="alternate" hrefLang="x-default" href={`https://lumotis.com/${industry.slug}`} />
        {hrefAlternates.map((h) => (
          <link key={h.code} rel="alternate" hrefLang={h.locale} href={h.href} />
        ))}
      </Helmet>

      <Navbar />

      <main data-testid={`industry-page-${industry.slug}`}>

        {/* ══════════════════════════════════════
            HERO
        ══════════════════════════════════════ */}
        <section className="relative bg-white pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden border-b border-zinc-100">
          {/* Dot grid */}
          <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
          {/* Gradient wash */}
          <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/8 via-transparent to-transparent pointer-events-none" />
          {/* Glow orb */}
          <div
            aria-hidden
            className="absolute -left-32 bottom-0 w-[480px] h-[480px] glow-orb bg-electric/10 pointer-events-none"
          />

          <div className="container relative">
            <div className="flex items-start justify-between gap-4 mb-8">
              <nav
                aria-label="Breadcrumb"
                className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 flex-wrap"
              >
                <Link to="/" className="hover:text-electric transition-colors">Home</Link>
                <ChevronRight className="w-3 h-3" />
                {isLocalised && (
                  <>
                    <Link to={`/${code}`} className="hover:text-electric transition-colors">
                      {market.shortName}
                    </Link>
                    <ChevronRight className="w-3 h-3" />
                  </>
                )}
                <Link to="/#services" className="hover:text-electric transition-colors">Industries</Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-charcoal">{industry.name}</span>
              </nav>
              <MarketSwitcher slug={industry.slug} currentCode={code} />
            </div>

            <div className="grid lg:grid-cols-12 gap-12 items-center">
              {/* Left — copy */}
              <div className="lg:col-span-7">
                {/* Eyebrow pill */}
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <div className="inline-flex items-center gap-2.5 border border-electric/25 bg-electric/8 rounded-full px-4 py-1.5">
                    <span className="text-xl leading-none" aria-hidden>{industry.emoji}</span>
                    <span className="text-overline text-electric">{industry.heroEyebrow}</span>
                  </div>
                  <MarketPill market={market} />
                </div>

                <motion.h1
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="font-display text-4xl sm:text-5xl lg:text-[62px] font-extrabold leading-[1.05] tracking-tight text-balance text-charcoal"
                >
                  {industry.heroHeadline}
                </motion.h1>

                {industry.heroAccent && (
                  <p className="mt-4 font-display text-xl md:text-2xl font-bold leading-tight">
                    <span className="text-gradient">{industry.heroAccent}</span>
                  </p>
                )}

                <p className="mt-6 text-lg text-zinc-500 leading-relaxed max-w-2xl">
                  {industry.heroTagline}
                  {isLocalised && (
                    <>
                      {" "}
                      <span className="text-charcoal font-semibold">
                        Built for {market.name} {industry.shortName.toLowerCase()} businesses
                      </span>{" "}
                      — local case studies, {market.currency.trim()} pricing, market-specific channel mix.
                    </>
                  )}
                </p>

                {/* CTAs */}
                <div className="mt-9 flex flex-wrap items-center gap-3">
                  <Link
                    to={isLocalised ? `/${code}#audit` : "/free-aso-audit"}
                    className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold rounded-xl hover:bg-electric-500 hover:-translate-y-0.5 transition-all shadow-lg shadow-electric/30"
                  >
                    {isLocalised
                      ? `Get Free ${market.shortName} Audit`
                      : "Get Free Marketing Audit"}{" "}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/case-studies"
                    className="inline-flex items-center gap-2 bg-white border border-zinc-200 text-charcoal px-6 py-3.5 font-semibold rounded-xl hover:border-electric hover:text-electric transition-colors"
                  >
                    See {industry.shortName} Results <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Stats row */}
                <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-4 max-w-2xl">
                  {industry.heroStats.map((s) => (
                    <div
                      key={s.label}
                      className="rounded-2xl border border-zinc-100 bg-white px-4 py-4 shadow-card"
                    >
                      <p className="font-display text-2xl md:text-3xl font-extrabold text-charcoal">
                        {s.value}
                      </p>
                      <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1 leading-snug">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — cover image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="lg:col-span-5"
              >
                <div className="relative">
                  <div className="absolute -inset-3 bg-electric/8 rounded-3xl -z-10" />
                  <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-zinc-100 bg-zinc-100 shadow-card-hover">
                    <img
                      src={industry.cover}
                      alt={`${industry.name} digital marketing`}
                      className="w-full h-full object-cover"
                      loading="eager"
                      width="640"
                      height="480"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 bg-electric text-white px-4 py-3 shadow-lg rounded-xl">
                    <p className="text-[10px] uppercase tracking-widest opacity-80">Specialist</p>
                    <p className="font-display font-bold text-lg">{industry.shortName}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            INSIGHTS
        ══════════════════════════════════════ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">01 — Why it matters</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  Why {industry.shortName} needs its own marketing playbook.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-500 leading-relaxed">
                Generic digital marketing doesn't work for {industry.shortName.toLowerCase()}. Three
                industry-specific realities change everything.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {industry.insightStats.map((s, idx) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                  className="rounded-2xl border border-zinc-100 bg-white p-7 md:p-8 flex flex-col shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all"
                >
                  <p className="font-display text-5xl md:text-6xl font-extrabold text-charcoal leading-none mb-3">
                    {s.value}
                  </p>
                  <p className="font-display font-bold text-charcoal text-base leading-snug mb-2">
                    {s.label}
                  </p>
                  <p className="text-sm text-zinc-500 leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            PILLARS / SERVICES
        ══════════════════════════════════════ */}
        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-100">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">02 — Our playbook</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  The digital marketing stack we run for {industry.shortName.toLowerCase()}.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-500 leading-relaxed">
                Six services. One coordinated revenue engine. Everything working together so results compound — not compete.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {industry.pillars.map((p, idx) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  className="group rounded-2xl border border-zinc-100 bg-white p-7 md:p-8 shadow-card hover:shadow-card-hover hover:border-electric/30 hover:-translate-y-1 transition-all"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="font-display font-extrabold text-2xl text-electric">
                      0{idx + 1}
                    </span>
                    <ArrowUpRight className="w-5 h-5 text-zinc-300 group-hover:text-electric transition-colors" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-charcoal group-hover:text-electric leading-snug mb-3 transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{p.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            CASE STUDY + RESULTS
        ══════════════════════════════════════ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">03 — Proof</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  Results we've delivered for {industry.shortName.toLowerCase()} businesses.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-500 leading-relaxed">
                Real metrics. Real clients. Real markets. Every number below is from a live engagement.
              </p>
            </div>

            <div className="grid lg:grid-cols-12 gap-5">
              {/* Featured case study */}
              <motion.article
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45 }}
                className="lg:col-span-7 rounded-2xl bg-charcoal text-white p-8 md:p-12 flex flex-col justify-between shadow-glow-brand"
              >
                <div>
                  <span className="inline-block text-overline text-electric mb-5">
                    {industry.caseStudy.badge}
                  </span>
                  <p className="font-display text-7xl md:text-8xl font-extrabold leading-none text-white tracking-tight">
                    {industry.caseStudy.metric}
                  </p>
                  <p className="mt-2 text-zinc-400 text-sm uppercase tracking-widest">
                    {industry.caseStudy.metricLabel}
                  </p>
                  <h3 className="mt-8 font-display text-2xl md:text-3xl font-bold leading-snug">
                    {industry.caseStudy.title}
                  </h3>
                  <p className="mt-4 text-zinc-300 leading-relaxed text-[15px]">
                    {industry.caseStudy.body}
                  </p>
                </div>
                <Link
                  to="/case-studies"
                  className="mt-8 inline-flex items-center gap-2 text-electric font-semibold hover:gap-3 transition-all w-fit"
                >
                  Read full case study <ArrowUpRight className="w-4 h-4" />
                </Link>
              </motion.article>

              {/* Results stack */}
              <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-5">
                {industry.results.map((r, idx) => (
                  <motion.div
                    key={r.label}
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: idx * 0.07 }}
                    className="rounded-2xl border border-zinc-100 bg-white p-6 md:p-7 shadow-card"
                  >
                    <p className="font-display text-3xl md:text-4xl font-extrabold text-charcoal leading-none">
                      {r.metric}
                    </p>
                    <p className="mt-2 text-charcoal font-semibold text-sm">{r.label}</p>
                    <p className="mt-1 text-xs text-zinc-400 uppercase tracking-widest">{r.market}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            TACTICS — dark section
        ══════════════════════════════════════ */}
        <section className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern-dark opacity-[0.06] pointer-events-none" />
          <div
            aria-hidden
            className="absolute -right-32 top-0 w-[500px] h-[500px] glow-orb bg-electric/15 pointer-events-none"
          />
          <div className="container relative">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">04 — Campaigns & tactics</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-balance leading-[1.05]">
                  What we actually run for {industry.shortName.toLowerCase()} businesses.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-400 leading-relaxed">
                A snapshot of the campaigns, channels and tactics deployed across every {industry.shortName.toLowerCase()} engagement.
              </p>
            </div>

            <div className="flex flex-wrap gap-2.5">
              {industry.tactics.map((tactic, idx) => (
                <motion.span
                  key={tactic}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: idx * 0.03 }}
                  className="inline-flex items-center gap-1.5 border border-white/15 bg-white/5 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-mono text-zinc-200 hover:border-electric hover:bg-electric/10 hover:text-white transition-colors"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-electric flex-shrink-0" />
                  {tactic}
                </motion.span>
              ))}
            </div>

            <div className="mt-10 inline-flex items-center gap-2 text-sm text-zinc-400">
              <Sparkles className="w-4 h-4 text-electric" />
              <span>Every engagement is fully custom — channels and tactics are scoped to your goals and market.</span>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            PROCESS — 5 steps
        ══════════════════════════════════════ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">05 — How we work</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  From audit to revenue — our 5-step process.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-500 leading-relaxed">
                The same operating cadence we use across every industry. No surprises, no black boxes — just a clear system that compounds.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
              {PROCESS_STEPS.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: idx * 0.06 }}
                    className="rounded-2xl border border-zinc-100 bg-white p-6 md:p-7 flex flex-col shadow-card"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <span className="font-mono text-xs text-electric tracking-widest font-bold">
                        {step.n}
                      </span>
                      <span className="w-9 h-9 grid place-items-center rounded-xl bg-electric/8 border border-electric/15">
                        <Icon className="w-4 h-4 text-electric" strokeWidth={2} />
                      </span>
                    </div>
                    <h3 className="font-display text-lg font-bold text-charcoal mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-zinc-500 leading-relaxed">{step.text}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            LOCAL TESTIMONIAL (LOCALISED ONLY)
        ══════════════════════════════════════ */}
        {isLocalised && market.testimonial && (
          <section className="py-20 md:py-28 bg-white border-t border-zinc-100" data-testid={`industry-local-${code}`}>
            <div className="container max-w-5xl">
              <div className="text-center mb-10">
                <p className="text-overline text-electric mb-3">
                  {market.flag} What {market.shortName} clients say
                </p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal leading-[1.05] text-balance">
                  Lumotis {industry.shortName} marketing — built for {market.name}.
                </h2>
              </div>
              <div className="rounded-2xl border border-zinc-100 bg-zinc-50 p-10 md:p-14 shadow-card text-center">
                <div className="flex items-center justify-center gap-0.5 mb-6">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-electric text-electric" />
                  ))}
                </div>
                <blockquote className="font-display text-2xl md:text-3xl text-charcoal leading-snug font-bold text-balance mb-8">
                  “{market.testimonial.text}”
                </blockquote>
                <p className="text-zinc-500 text-sm font-medium">
                  — {market.testimonial.name},{" "}
                  <span className="text-zinc-400">{market.testimonial.role}</span>
                </p>
              </div>
              {market.localChannels && market.localChannels.length > 0 && (
                <div className="mt-10 flex flex-wrap items-center justify-center gap-2.5">
                  <span className="text-xs uppercase tracking-widest font-bold text-zinc-400 mr-2">
                    {market.shortName} channels:
                  </span>
                  {market.localChannels.map((ch) => (
                    <span
                      key={ch}
                      className="inline-flex items-center gap-1.5 border border-zinc-100 bg-white rounded-full px-3 py-1.5 text-xs text-zinc-700"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-electric" />
                      {ch}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* ══════════════════════════════════════
            FAQ
        ══════════════════════════════════════ */}
        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-100">
          <div className="container grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="text-overline text-electric mb-3">06 — FAQ</p>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                Common questions about {industry.shortName} marketing.
              </h2>
              <p className="mt-5 text-zinc-500 leading-relaxed">
                Have a question specific to your {industry.shortName.toLowerCase()} business? Drop it in the audit form — a real strategist replies within 24h.
              </p>
              <Link
                to={isLocalised ? `/${code}#audit` : "/free-aso-audit"}
                className="mt-8 inline-flex items-center gap-2 bg-electric text-white px-6 py-3 font-semibold rounded-xl hover:bg-electric-500 transition-colors shadow-lg shadow-electric/25"
              >
                {isLocalised ? `Ask the ${market.shortName} team` : "Ask the team"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-8">
              <FAQAccordion items={industry.faq} />
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            RELATED INDUSTRIES
        ══════════════════════════════════════ */}
        {related.length > 0 && (
          <section className="py-20 md:py-28 bg-white">
            <div className="container">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                <div>
                  <p className="text-overline text-electric mb-3">07 — Other industries</p>
                  <h2 className="font-display text-3xl md:text-4xl font-extrabold text-charcoal leading-[1.05]">
                    Other industries we specialise in.
                  </h2>
                </div>
                <Link
                  to="/#services"
                  className="inline-flex items-center gap-2 border border-zinc-200 text-charcoal px-5 py-2.5 text-sm font-semibold rounded-xl hover:border-electric hover:text-electric transition-colors w-fit"
                >
                  View all services <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {related.map((r, idx) => (
                  <motion.div
                    key={r.slug}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: idx * 0.06 }}
                  >
                    <Link
                      to={isLocalised ? `/${code}/${r.slug}` : `/${r.slug}`}
                      className="group rounded-2xl border border-zinc-100 bg-white p-7 md:p-8 flex flex-col h-full shadow-card hover:shadow-card-hover hover:border-electric/30 hover:-translate-y-1 transition-all"
                    >
                      <span className="text-3xl mb-5" aria-hidden>{r.emoji}</span>
                      <h3 className="font-display text-xl font-bold text-charcoal group-hover:text-electric leading-snug transition-colors">
                        {r.name}
                      </h3>
                      <p className="mt-2 text-sm text-zinc-500 leading-relaxed flex-1">
                        {r.heroAccent}
                      </p>
                      <span className="mt-6 inline-flex items-center gap-1 text-xs font-bold text-zinc-400 group-hover:text-electric uppercase tracking-widest transition-colors">
                        View playbook <ArrowUpRight className="w-3.5 h-3.5" />
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        <CTABanner />
      </main>

      <Footer />
    </>
  );
}
